import React, { useState } from 'react';
import { Save, Copy, Loader2, FileText } from 'lucide-react';
import Modal from './Modal';

interface SaveTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (action: 'update' | 'saveAsNew', name: string, description: string) => Promise<void>;
  isExistingTemplate: boolean;
  currentTemplateName?: string;
  currentTemplateDescription?: string;
  loading?: boolean;
}

export default function SaveTemplateModal({
  isOpen,
  onClose,
  onSave,
  isExistingTemplate,
  currentTemplateName = '',
  currentTemplateDescription = '',
  loading = false
}: SaveTemplateModalProps) {
  const [name, setName] = useState(currentTemplateName);
  const [description, setDescription] = useState(currentTemplateDescription);
  const [selectedAction, setSelectedAction] = useState<'update' | 'saveAsNew'>(
    isExistingTemplate ? 'update' : 'saveAsNew'
  );

  // Reset form when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setName(currentTemplateName);
      setDescription(currentTemplateDescription);
      setSelectedAction(isExistingTemplate ? 'update' : 'saveAsNew');
    }
  }, [isOpen, currentTemplateName, currentTemplateDescription, isExistingTemplate]);

  const handleSave = async () => {
    if (!name.trim()) {
      alert('Please enter a template name');
      return;
    }

    try {
      await onSave(selectedAction, name.trim(), description.trim());
      onClose();
    } catch (error) {
      console.error('Error saving template:', error);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Save Template"
      size="md"
      closeOnOverlayClick={!loading}
    >
      <div className="space-y-6">
        {/* Action Selection (only show if editing existing template) */}
        {isExistingTemplate && (
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              What would you like to do?
            </label>
            <div className="space-y-2">
              <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  name="action"
                  value="update"
                  checked={selectedAction === 'update'}
                  onChange={(e) => setSelectedAction(e.target.value as 'update')}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  disabled={loading}
                />
                <div className="ml-3 flex items-center">
                  <Save className="h-4 w-4 text-blue-600 mr-2" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Update existing template</div>
                    <div className="text-xs text-gray-500">Overwrite the current template with your changes</div>
                  </div>
                </div>
              </label>
              
              <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  name="action"
                  value="saveAsNew"
                  checked={selectedAction === 'saveAsNew'}
                  onChange={(e) => setSelectedAction(e.target.value as 'saveAsNew')}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  disabled={loading}
                />
                <div className="ml-3 flex items-center">
                  <Copy className="h-4 w-4 text-green-600 mr-2" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Save as new template</div>
                    <div className="text-xs text-gray-500">Create a new template and keep the original</div>
                  </div>
                </div>
              </label>
            </div>
          </div>
        )}

        {/* Template Name */}
        <div>
          <label htmlFor="templateName" className="block text-sm font-medium text-gray-700 mb-2">
            Template Name *
          </label>
          <input
            id="templateName"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter template name..."
            disabled={loading || (selectedAction === 'update' && isExistingTemplate)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
          />
          {selectedAction === 'update' && isExistingTemplate && (
            <p className="text-xs text-gray-500 mt-1">Name cannot be changed when updating existing template</p>
          )}
        </div>

        {/* Template Description */}
        <div>
          <label htmlFor="templateDescription" className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            id="templateDescription"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter template description (optional)..."
            rows={3}
            disabled={loading}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          
          <button
            onClick={handleSave}
            disabled={loading || !name.trim()}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                {selectedAction === 'update' ? (
                  <Save className="h-4 w-4 mr-2" />
                ) : (
                  <FileText className="h-4 w-4 mr-2" />
                )}
                {selectedAction === 'update' ? 'Update Template' : 'Save as New Template'}
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
}
